/**
 * Card de "Brecha vs el mejor de tu grupo" — segunda tarjeta de Capa 2.
 *
 * Muestra dos barras horizontales (muni vs peer best) + la brecha en
 * COP en grande. Si tu muni es el #1 del peer group, en lugar de brecha
 * se muestra "Eres el mejor" con el `gap` formateado en `—`.
 *
 * En la ficha de otro municipio (`municipioAjeno`, issue #746) la tarjeta
 * lo nombra y habla de "su grupo", igual que el hero desde el #726.
 *
 * Cálculo de la brecha con `BigInt` porque los montos SGR son grandes
 * (>1B COP) y `Number` perdería precisión si no truncáramos los
 * decimales que devuelve el backend (`Decimal` serializado como string).
 */

import { TrendingUp } from "lucide-react";

import { NombreMunicipio } from "@/components/NombreMunicipio";
import { type MunicipalityScoreDTO } from "@/lib/api";

import { formatCop } from "./_shared";

export function GapVsPeerBest({
  score,
  municipioAjeno,
}: {
  score: MunicipalityScoreDTO;
  /** Nombre del municipio de la ficha cuando no es el del usuario; null en la propia. */
  municipioAjeno: string | null;
}) {
  const grupo = municipioAjeno ? "su grupo" : "tu grupo";
  // Sin mejor par no hay contra quién medir. Un municipio sin proyectos SGR
  // (`has_score` false) sí la ve: el backend calcula su grupo igual, y es la
  // comparación que su hero promete (issue #561).
  if (score.peer_best_dane === null) {
    return null;
  }
  const muni = BigInt(score.captacion_total_cop.replace(/\.[0-9]+$/, ""));
  const best = BigInt(score.peer_best_captacion_cop.replace(/\.[0-9]+$/, ""));
  const gap = best - muni;
  const gapPositive = gap > 0n;
  // Ratio del muni vs el peer best, para la barra visual. Cuando muni
  // es el top (gap <= 0), va al 100%; sino, proporción real con 1
  // decimal de precisión vía multiplicación por 1000n.
  const muniRatio = gapPositive ? Number((muni * 1000n) / best) / 10 : 100;

  return (
    <div
      data-testid="ficha-gap"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <div className="flex items-start gap-2">
        <TrendingUp className="mt-0.5 h-4 w-4 text-brand-600" aria-hidden="true" />
        <h2 className="text-sm font-bold text-slate-900">Brecha vs el mejor de {grupo}</h2>
      </div>

      {/* Comparación visual horizontal — muestra dónde está tu muni
          relativo al mejor del grupo de comparación. Más intuitivo
          que solo texto. */}
      <div className="mt-4 space-y-3">
        <div>
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-slate-500">
              {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu muni"}
            </span>
            <span className="font-semibold text-slate-900 tabular-nums">{formatCop(muni)}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-brand-500" style={{ width: `${Math.max(2, muniRatio)}%` }} />
          </div>
        </div>
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-xs">
            <span className="text-slate-500">
              {score.peer_best_name ? (
                <>
                  Mejor:{" "}
                  <span className="font-semibold text-slate-700 capitalize">
                    {score.peer_best_name.toLowerCase()}
                  </span>
                  {score.peer_best_department && (
                    <span className="text-slate-500 capitalize">
                      {" "}
                      ({score.peer_best_department.toLowerCase()})
                    </span>
                  )}
                </>
              ) : (
                `Mejor de ${grupo}`
              )}
            </span>
            <span className="font-semibold text-slate-900 tabular-nums">{formatCop(best)}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-full bg-emerald-500" />
          </div>
        </div>
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-slate-700">
            {gapPositive ? "Brecha" : municipioAjeno ? "Es el mejor" : "Eres el mejor"}
          </span>
          <span
            className={`text-lg font-extrabold tabular-nums ${
              gapPositive ? "text-red-700" : "text-emerald-700"
            }`}
          >
            {gapPositive ? formatCop(gap) : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
